import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, ApiError } from "@/api/client";
import { reset as resetNotifications } from "@/lib/notifications";

const AuthContext = createContext(null);
const USER_KEY = "aiqa-user";

// Landing route per role — used after login/signup and by the guard.
// Where a role lands after signing in.
//
// This used to fall through to the tester dashboard for anything it didn't
// recognise, which means a role typo — or a role the frontend hasn't been taught
// yet — silently handed out the tester surface. Now it's an explicit map, and
// anything unknown goes to /no-access.
const HOME = {
  superadmin: "/admin",
  admin: "/admin-co",
  project_manager: "/pm",
  developer: "/developer",
  tester: "/dashboard",
};

export function homeForRole(role) {
  return HOME[role] || "/no-access";
}

// The cached user follows the TOKEN's storage, so "Remember me" unticked really
// does leave nothing behind after the tab closes. It used to be written to
// localStorage unconditionally, which left the previous user's name, email and
// role on a shared machine even when they had asked not to be remembered.
function readUser() {
  try {
    const raw =
      localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    return JSON.parse(raw || "null");
  } catch (e) {
    return null;
  }
}

function writeUser(nextUser, remember) {
  try {
    // Clear both first so switching persistence never leaves a stale copy —
    // same contract as setToken().
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
    if (nextUser) {
      const store = remember ? localStorage : sessionStorage;
      store.setItem(USER_KEY, JSON.stringify(nextUser));
    }
  } catch (e) {
    /* storage unavailable — ignore */
  }
}

export function AuthProvider({ children }) {
  const [token, setTok] = useState(() => getToken());
  const [user, setUser] = useState(readUser);

  // The api client broadcasts this on any 401 — drop the session everywhere.
  useEffect(() => {
    const onUnauthorized = () => {
      setTok(null);
      setUser(null);
      writeUser(null);
    };
    window.addEventListener("aiqa:unauthorized", onUnauthorized);
    return () => window.removeEventListener("aiqa:unauthorized", onUnauthorized);
  }, []);

  const persist = useCallback((nextToken, nextUser, remember = true) => {
    setToken(nextToken, { session: !remember });
    setTok(nextToken);
    setUser(nextUser);
    writeUser(nextUser, remember);
  }, []);

  // Real login → POST /auth/login, then GET /auth/me for the full profile+role.
  // If the backend is unreachable we fall back to a local "demo" session so the
  // UI is usable during frontend dev.
  const login = useCallback(
    async (email, password, { remember = true } = {}) => {
      try {
        // `remember` reaches the server too: it decides the TOKEN'S LIFETIME
        // there. Choosing the browser store alone left a remembered session dead
        // after the default hour, which is what made the checkbox look broken.
        const res = await api.post("/auth/login", { email, password, remember });
        // 2FA is on for this account → hand the challenge back to the caller,
        // which collects a TOTP code and calls verify2fa(). No session yet.
        if (res?.twofa_required) {
          return { demo: false, twofaRequired: true, twofaToken: res.twofa_token };
        }
        // Temporarily persist the token so the /me call can authenticate.
        setToken(res.access_token, { session: !remember });
        setTok(res.access_token);
        // Fetch the full user object (includes role, name, id).
        let fullUser = { email };
        try {
          fullUser = await api.get("/auth/me");
        } catch (_) {
          /* /me failed — still authenticated, use email only */
        }
        persist(res.access_token, fullUser, remember);
        return { demo: false, user: fullUser };
      } catch (e) {
        // Surface EVERY real backend response to the user: bad creds (401),
        // forbidden (403), and a network/timeout failure (ApiError status 0).
        // The old code silently created a fake "demo" session on anything but
        // 401 — so a momentary blip (backend still starting, slow first request)
        // stored a bogus `demo.<email>` token that then made every later call
        // fail with "backend not reachable", trapping the app in demo mode.
        // Only a non-ApiError JS exception falls back to the offline demo
        // session — and never on a local/dev origin, where the backend is
        // expected to be real: a phantom demo session there just traps the app
        // in "backend not reachable" with a bogus token the API always rejects.
        if (e instanceof ApiError) throw e;
        const isLocalOrigin =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        if (isLocalOrigin) throw e;
        const demoUser = { email, role: "tester", demo: true };
        persist(`demo.${btoa(email)}`, demoUser, remember);
        return { demo: true, user: demoUser };
      }
    },
    [persist]
  );

  // Second step of a 2FA login: exchange (challenge token + TOTP code) for a
  // real session token, then establish the session like a normal login.
  const verify2fa = useCallback(
    async (twofaToken, code, { remember = true } = {}) => {
      // The session token is minted at THIS step for a 2FA account, so the
      // remember choice has to travel with the code.
      const res = await api.post("/auth/2fa/verify", { twofa_token: twofaToken, code, remember });
      setToken(res.access_token, { session: !remember });
      setTok(res.access_token);
      let fullUser = {};
      try { fullUser = await api.get("/auth/me"); } catch (_) { /* keep going */ }
      persist(res.access_token, fullUser, remember);
      return { user: fullUser };
    },
    [persist]
  );

  // Public self-signup → the new account is a company ADMIN (owner). With email
  // verification ON (default) this does NOT create a session — it triggers an
  // emailed OTP and returns { verified: false, email, resendIn, expiresIn }; the
  // caller then collects the code and calls verifyEmailOtp(). With verification
  // OFF the backend returns a token and we sign in immediately.
  const register = useCallback(
    async (email, password, { name, companyName, ref, plan } = {}) => {
      try {
        const res = await api.post("/auth/register", {
          email,
          password,
          name: name || null,
          company_name: companyName || null,
          ref: ref || null,
          plan: plan || null,
        });
        if (res?.status === "active" && res.access_token) {
          persist(res.access_token, res.user || { email, role: "admin", name });
          return { verified: true, user: res.user };
        }
        return {
          verified: false,
          email: res?.email || email,
          resendIn: res?.resend_in ?? 60,
          expiresIn: res?.expires_in ?? 600,
        };
      } catch (e) {
        // Any real HTTP error (email taken, SMTP down, etc.) must surface.
        if (e instanceof ApiError && e.status && e.status !== 0) throw e;
        // Connectivity failure → offline demo session so frontend dev works.
        const demoUser = { email, role: "admin", name, demo: true };
        persist(`demo.${btoa(email)}`, demoUser);
        return { verified: true, demo: true, user: demoUser };
      }
    },
    [persist]
  );

  // Confirm the signup OTP → the backend creates the account and returns a
  // token; we establish the session and return the user. Errors propagate so
  // the signup screen can show "incorrect code", "expired", etc.
  const verifyEmailOtp = useCallback(
    async (email, code, ref) => {
      const res = await api.post("/auth/verify-email", { email, code, ref: ref || null });
      const fullUser = res?.user || { email, role: "admin" };
      persist(res.access_token, fullUser);
      // requiresPayment/plan tell Signup to show the registration payment step
      // (paid-pack signup) instead of entering the app on the free trial.
      return { user: fullUser, requiresPayment: !!res?.requires_payment, plan: res?.plan || null };
    },
    [persist]
  );

  // Ask the backend to email a fresh code for a pending signup.
  const resendSignupOtp = useCallback(async (email) => {
    const res = await api.post("/auth/resend-otp", { email });
    return { resendIn: res?.resend_in ?? 60, expiresIn: res?.expires_in ?? 600 };
  }, []);

  // Establish a session from an access token we already hold (e.g. after a
  // tester accepts an invite, which returns a token directly). Fetches the full
  // profile so role-based routing works.
  const loginWithToken = useCallback(
    async (accessToken) => {
      setToken(accessToken);
      setTok(accessToken);
      let fullUser = null;
      try {
        fullUser = await api.get("/auth/me");
      } catch (_) { /* keep token; minimal user */ }
      persist(accessToken, fullUser || { role: "tester" });
      return { user: fullUser };
    },
    [persist]
  );

  // Dropping the session must also drop the notifications store, or the next
  // user to sign in in this tab sees the previous user's rows and unread badge.
  // Token first, so a poll that fires afterwards has nothing to authenticate with.
  const logout = useCallback(() => {
    persist(null, null);
    resetNotifications();
  }, [persist]);

  // ---- Support impersonation ------------------------------------------------
  // Starting one used to simply OVERWRITE the operator's own session: their
  // token and user were replaced, no banner said so anywhere in the app, and the
  // only way back was signing out and in again. The operator's session is now
  // stashed so it can be restored in one click.
  //
  // sessionStorage, not localStorage, on purpose: an impersonation must not
  // survive a browser restart, and it must not leak into another tab.
  const IMPERSONATOR_KEY = "aiqa-impersonator";
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = sessionStorage.getItem(IMPERSONATOR_KEY);
      return raw ? JSON.parse(raw).viewing || null : null;
    } catch (_) { return null; }
  });

  const startImpersonation = useCallback(async (accessToken, viewing) => {
    try {
      sessionStorage.setItem(IMPERSONATOR_KEY, JSON.stringify({
        token, user, viewing,
        // Everything needed to put the operator back exactly where they were.
        startedAt: Date.now(),
      }));
    } catch (_) { /* storage unavailable — the session still works, minus the exit */ }
    setToken(accessToken, { session: true });
    setTok(accessToken);
    let fullUser = null;
    try { fullUser = await api.get("/auth/me"); } catch (_) { /* keep the token */ }
    setUser(fullUser || { role: viewing?.role || "admin" });
    writeUser(fullUser, /* remember */ false);
    setImpersonation(viewing || null);
    return fullUser;
  }, [token, user]);

  const stopImpersonation = useCallback(() => {
    let stash = null;
    try {
      const raw = sessionStorage.getItem(IMPERSONATOR_KEY);
      stash = raw ? JSON.parse(raw) : null;
      sessionStorage.removeItem(IMPERSONATOR_KEY);
    } catch (_) { /* ignore */ }
    setImpersonation(null);
    resetNotifications();
    if (!stash || !stash.token) {
      // The operator's own token is gone (expired, or storage was cleared).
      // Signing them out is the honest outcome — better than leaving them inside
      // a customer's account with a broken exit.
      persist(null, null);
      return false;
    }
    persist(stash.token, stash.user, /* remember */ true);
    return true;
  }, [persist]);

  // Swap in a token the server just minted for the SAME session — currently only
  // after a password change, which bumps the account's token_version and thereby
  // retires every token issued before it, this tab's included. Without adopting
  // the replacement, changing your password signs you out of the tab you are
  // standing in on the very next request.
  //
  // The persistence choice ("remember me") is inferred from where the current
  // token lives, so a session-only sign-in stays session-only.
  const adoptToken = useCallback((nextToken) => {
    if (!nextToken) return;
    let sessionOnly = false;
    try {
      sessionOnly = !!sessionStorage.getItem("aiqa-token");
    } catch (_) { /* storage unavailable — default to persistent */ }
    setToken(nextToken, { session: sessionOnly });
    setTok(nextToken);
  }, []);

  // Expose a way to refresh user data after profile edits.
  const refreshUser = useCallback(async () => {
    try {
      const fullUser = await api.get("/auth/me");
      setUser(fullUser);
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
      } catch (e) {
        // In memory the user is current; only the cached copy is stale, so the
        // next reload shows the pre-edit values back again.
        console.debug("[aiqa/auth] caching the refreshed user failed "
                      + "(continuing):", e);
      }
    } catch (e) {
      // Callers use this after a profile edit and do not await a result, so a
      // failure here leaves the UI showing the OLD user with no error at all.
      console.debug("[aiqa/auth] refreshing the signed-in user failed "
                    + "(continuing):", e);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token, user, isAuthenticated: !!token,
        login, verify2fa, register, verifyEmailOtp, resendSignupOtp, loginWithToken,
        logout, refreshUser, adoptToken,
        // `impersonation` is null in a normal session. It is ALSO derived from the
        // server (`user.impersonated_by`) so the banner survives a page reload,
        // when this in-memory flag is gone.
        impersonation: impersonation || (user?.impersonated_by ? { unknown: true } : null),
        isImpersonating: !!impersonation || !!user?.impersonated_by,
        startImpersonation, stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
